<template>
    <div class="chat-header">
        <div class="header-left">
            <button v-if="showBackButton" class="back-btn" @click="$emit('back')">
                <i class="material-icons">arrow_back</i>
            </button>

            <div class="participant-info" v-if="participant">
                <div class="avatar">
                    <img
                        v-if="participant.avatar"
                        :src="participant.avatar"
                        :alt="participantName"
                    />
                    <span v-else class="avatar-initial">{{ avatarInitial }}</span>
                    <ChatOnlineStatus
                        :is-online="isOnline"
                        size="sm"
                        class="avatar-status"
                    />
                </div>

                <div class="participant-details">
                    <h3 class="participant-name">{{ participantName }}</h3>
                    <span class="participant-status">
                        <template v-if="isTyping">
                            <span class="typing-text">est en train d'écrire...</span>
                        </template>
                        <template v-else-if="isOnline">
                            En ligne
                        </template>
                        <template v-else-if="lastSeen">
                            Vu {{ formatLastSeen(lastSeen) }}
                        </template>
                        <template v-else>
                            {{ participantRole }}
                        </template>
                    </span>
                </div>
            </div>
        </div>

        <div class="header-right">
            <button
                v-if="showInfoButton"
                class="header-action-btn"
                @click="$emit('show-info')"
                title="Informations"
            >
                <i class="material-icons">info_outline</i>
            </button>

            <button
                v-if="showMenuButton"
                class="header-action-btn"
                @click="toggleMenu"
                title="Plus d'options"
            >
                <i class="material-icons">more_vert</i>
            </button>

            <!-- Dropdown menu -->
            <div v-if="menuOpen" class="header-menu" v-click-outside="closeMenu">
                <button class="menu-item" @click="handleMenuAction('archive')">
                    <i class="material-icons">archive</i>
                    <span>Archiver la conversation</span>
                </button>
                <button class="menu-item" @click="handleMenuAction('mute')">
                    <i class="material-icons">notifications_off</i>
                    <span>Désactiver les notifications</span>
                </button>
                <button class="menu-item danger" @click="handleMenuAction('block')">
                    <i class="material-icons">block</i>
                    <span>Bloquer</span>
                </button>
            </div>

            <button
                v-if="showCloseButton"
                class="header-action-btn close-btn"
                @click="$emit('close')"
                title="Fermer"
            >
                <i class="material-icons">close</i>
            </button>
        </div>
    </div>
</template>

<script>
import ChatOnlineStatus from './ChatOnlineStatus.vue';

export default {
    name: 'ChatHeader',
    components: {
        ChatOnlineStatus
    },
    props: {
        participant: {
            type: Object,
            default: null
        },
        isOnline: {
            type: Boolean,
            default: false
        },
        isTyping: {
            type: Boolean,
            default: false
        },
        lastSeen: {
            type: [Date, String],
            default: null
        },
        showBackButton: {
            type: Boolean,
            default: false
        },
        showInfoButton: {
            type: Boolean,
            default: true
        },
        showMenuButton: {
            type: Boolean,
            default: true
        },
        showCloseButton: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            menuOpen: false
        };
    },
    computed: {
        participantName() {
            if (!this.participant) return '';
            return this.participant.fullName ||
                   `${this.participant.firstName || ''} ${this.participant.lastName || ''}`.trim() ||
                   this.participant.email ||
                   'Utilisateur';
        },
        participantRole() {
            if (!this.participant) return '';
            const roleMap = {
                'Seller': 'Vendeur',
                'AccountManager': 'Account Manager',
                'Admin': 'Administrateur'
            };
            return roleMap[this.participant.type] || this.participant.type || '';
        },
        avatarInitial() {
            if (!this.participant) return '?';
            const name = this.participantName;
            return name.charAt(0).toUpperCase();
        }
    },
    methods: {
        toggleMenu() {
            this.menuOpen = !this.menuOpen;
        },
        closeMenu() {
            this.menuOpen = false;
        },
        handleMenuAction(action) {
            this.$emit('menu-action', action);
            this.closeMenu();
        },
        formatLastSeen(date) {
            if (!date) return '';

            const lastSeenDate = new Date(date);
            const now = new Date();
            const diffMs = now - lastSeenDate;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) {
                return "à l'instant";
            } else if (diffMins < 60) {
                return `il y a ${diffMins} min`;
            } else if (diffHours < 24) {
                return `il y a ${diffHours}h`;
            } else if (diffDays < 7) {
                return `il y a ${diffDays}j`;
            } else {
                return lastSeenDate.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short'
                });
            }
        }
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (event) => {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el._clickOutside);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutside);
            }
        }
    }
};
</script>

<style scoped>
.chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: #ffffff;
    border-bottom: 1px solid #e5e7eb;
    min-height: 64px;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.back-btn:hover {
    background-color: #f3f4f6;
    color: #374151;
}

.participant-info {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.avatar {
    position: relative;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
}

.avatar img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}

.avatar-initial {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    font-weight: 600;
    font-size: 16px;
}

.avatar-status {
    position: absolute;
    bottom: 0;
    right: 0;
}

.participant-details {
    min-width: 0;
}

.participant-name {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.participant-status {
    font-size: 12px;
    color: #6b7280;
}

.typing-text {
    color: #059669;
    font-style: italic;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 4px;
    position: relative;
}

.header-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.header-action-btn:hover {
    background-color: #f3f4f6;
    color: #374151;
}

.close-btn:hover {
    background-color: #fee2e2;
    color: #dc2626;
}

.header-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 220px;
    z-index: 100;
    overflow: hidden;
}

.menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    color: #374151;
    font-size: 14px;
    transition: background-color 0.2s;
}

.menu-item:hover {
    background-color: #f3f4f6;
}

.menu-item i {
    font-size: 20px;
    color: #6b7280;
}

.menu-item.danger {
    color: #dc2626;
}

.menu-item.danger i {
    color: #dc2626;
}

.menu-item.danger:hover {
    background-color: #fef2f2;
}
</style>
